// https://stackoverflow.com/a/75093937
export const debounce = (func, delay = 600, immediate = false) => {
	let timeout;
	return function () {
		const context = this;
		const args = arguments;
		const later = function () {
			timeout = null;
			if (!immediate) func.apply(context, args);
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, delay);
		if (callNow) func.apply(context, args);
	};
};


export const toTitleCase = (str) => {
	return str.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
}

export const isEmpty = (value) => {
	// Check for null, undefined, or empty string
	if (value === null || value === undefined || value === "") return true;

	// Check for empty array
	if (Array.isArray(value) && value.length === 0) return true;

	// Check for empty object
	if (typeof value === "object" && Object.keys(value).length === 0) return true;

	// Check for NaN
	if (typeof value === "number" && isNaN(value)) return true;

	// For anything else, return false (value is not empty)
	return false;
}

// https://stackoverflow.com/a/5723274
export const truncateLongText = (fullStrLen, strLenLimit, separator) => {
	if (fullStrLen.length <= strLenLimit) return fullStrLen;

	separator = separator || "...";

	var sepLen = separator.length,
		charsToShow = strLenLimit - sepLen,
		frontChars = Math.ceil(charsToShow / 2),
		backChars = Math.floor(charsToShow / 2);

	return fullStrLen.substr(0, frontChars) + separator + fullStrLen.substr(fullStrLen.length - backChars);
};

// display icon based on file extension
export const getFileTypeIcon = (fileExt) => {
	// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
	let archive = ["arc", "bz", "bz2", "gz", "jar", "rar", "tar", "zip", "7z"];
	let audio = ["aac", "mid", "midi", "mp3", "oga", "opus", "wav", "weba"];
	let document = ["abw", "doc", "docx", "odt", "pdf"];
	let file = ["azw", "bin", "cda", "csh", "eot", "epub", "mpkg", "ogx", "otf", "sh", "ttf", "vsd", "woff", "woff2"];
	let image = ["avif", "bmp", "gif", "ico", "jpeg", "jpg", "png", "svg", "tif", "tiff", "webp"];
	let presentation = ["odp", "ppt", "pptx"];
	let spreadsheet = ["ods", "xls", "xlsx"];
	let text = ["css", "csv", "htm", "html", "ics", "js", "json", "jsonld", "mjs", "php", "rtf", "txt", "xhtml", "xml", "xul", "yaml"];
	let video = ["avi", "mkv", "mp4", "mpeg", "ogg", "ogv", "ts", "webm", "3gp", "3g2"];

	let fileTypeList = [
		{ type: "archive", values: archive },
		{ type: "audio", values: audio },
		{ type: "document", values: document },
		{ type: "file", values: file },
		{ type: "image", values: image },
		{ type: "presentation", values: presentation },
		{ type: "spreadsheet", values: spreadsheet },
		{ type: "text", values: text },
		{ type: "video", values: video },
	];

	let fileType = fileExt.replaceAll(".", "").trim().toLowerCase();
	let isImgExists = true

	// check if image icon is existing from folder, then use it else, check conditions below
	try {
		require("@/assets/img/file_type_icons/" + fileType + ".png");
	} catch (e) {
		isImgExists = false
	}

	if (!isImgExists) {

		fileType = fileTypeList.filter((type) => {
			return type.values.includes(fileType)
		}).map((e) => { return e.type }).toString()

		if (fileType == "document") {
			fileType = "doc";
		} else if (fileType == "image") {
			fileType = "jpg";
		} else if (fileType == "spreadsheet") {
			fileType = "xls";
		} else if (fileType == "presentation") {
			fileType = "ppt";
		} else if (fileType == "text") {
			fileType = "txt";
		} else if (fileType.trim() == "") {
			fileType = "file";
		}


	}

	return fileType + ".png";
}

export const sortAlphabetically = (arr, field) => {

	return arr.sort(function (a, b) {

		if (a && b) {
			let x = field(a).toLowerCase()
			let y = field(b).toLowerCase()

			if (x < y)
				//sort string ascending
				return -1

			if (x > y) return 1

			return 0 //default return value (no sorting)
		}


	})
}

export const formatCurrency = (value) => {
	const numericValue = parseFloat(value);
	if (isNaN(numericValue)) {
		return '$0.00';
	}
	return `$${numericValue.toFixed(2)}`;
}

// src/helpers/logHelper.js

/**
 * A helper function for conditional console logging based on the environment.
 *
 * @param {...any} messages - The messages to log to the console.
 * @param {boolean} [forceLog=false] - If true, the message will be logged even in production.
 */
export function log(...messages) {
	const lastArg = messages[messages.length - 1];
	const forceLog = typeof lastArg === 'boolean' ? messages.pop() : false; // Extract forceLog if it's the last argument

	// Check if the current environment is not production OR if forceLog is true
	if (process.env.NODE_ENV !== 'production' || forceLog) {
		console.log(...messages);
	}
}

/**
 * A helper function for conditional console warnings based on the environment.
 *
 * @param {...any} messages - The messages to warn to the console.
 * @param {boolean} [forceLog=false] - If true, the message will be warned even in production.
 */
export function warn(...messages) {
	const lastArg = messages[messages.length - 1];
	const forceLog = typeof lastArg === 'boolean' ? messages.pop() : false;

	if (process.env.NODE_ENV !== 'production' || forceLog) {
		console.warn(...messages);
	}
}

/**
 * A helper function for conditional console errors based on the environment.
 *
 * @param {...any} messages - The messages to error to the console.
 * @param {boolean} [forceLog=false] - If true, the message will be errored even in production.
 */
export function error(...messages) {
	const lastArg = messages[messages.length - 1];
	const forceLog = typeof lastArg === 'boolean' ? messages.pop() : false;

	if (process.env.NODE_ENV !== 'production' || forceLog) {
		console.error(...messages);
	}
}

export function toOptionName(count) {
    // Handle the special case: if count is 1, return an empty string
    if (count <= 1) {
        return '';
    }

    let result = '';
    let num = count; // Start with the actual count for conversion

    while (num > 0) {
        // Calculate the remainder when dividing by 26
        // This gives us the position of the current letter (1 for A, 26 for Z)
        let remainder = num % 26;

        // Adjustment for 'Z' and subsequent columns:
        // If remainder is 0, it means the letter is 'Z' (the 26th letter)
        // and we need to decrement num by 26 to properly carry over for the next iteration.
        if (remainder === 0) {
            remainder = 26; // Map 0 remainder to the 26th letter ('Z')
        }

        // Convert the remainder to a character
        // 'A' has ASCII value 65. So, (remainder - 1) + 65 gives us the correct character code
        result = String.fromCharCode(64 + remainder) + result;

        // Update num for the next iteration
        num = Math.floor((num - remainder) / 26);
    }

    return result;
}

export * from './emailHelper.js';
